/**
 * ForgeContext — loads and caches the FORGE workspace summary for one tender
 * and exposes a refresh() that panels call after mutations.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { forgeAPI } from '../../services/api';

const ForgeContext = createContext(null);

export const useForge = () => {
  const ctx = useContext(ForgeContext);
  if (!ctx) throw new Error('useForge must be used within ForgeProvider');
  return ctx;
};

export const ForgeProvider = ({ tenderId, children }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const preparedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!tenderId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await forgeAPI.getSummary(tenderId);
      setSummary(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load FORGE data');
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  // Quiet re-fetch (no loading flag) for background polling — updates the summary
  // in place without spinners/flicker.
  const silentRefresh = useCallback(async () => {
    if (!tenderId || (typeof document !== 'undefined' && document.hidden)) return;
    try {
      const data = await forgeAPI.getSummary(tenderId);
      setSummary(data);
    } catch { /* transient — keep last good summary */ }
  }, [tenderId]);

  useEffect(() => { preparedRef.current = false; }, [tenderId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live updates: Accio writes suppliers/chats/quotes back to FORGE asynchronously
  // (Alibaba outreach + log_negotiation), so poll the summary while the cockpit/
  // workspace is open. Otherwise "Suppliers & chats" (and quotes/status) stay
  // stale until a manual refresh. Pauses when the tab is hidden.
  useEffect(() => {
    const id = setInterval(silentRefresh, 15000);
    return () => clearInterval(id);
  }, [silentRefresh]);

  // Auto-prepare on first FORGE entry: generate the spec sheet (if missing),
  // build bid requirements, and auto-trigger sourcing/discovery (server-side,
  // in the background) when no candidates exist yet — then reload. Runs once per
  // tender. The backend is idempotent, so calling it every first-mount is safe.
  useEffect(() => {
    if (!summary || preparedRef.current) return;
    preparedRef.current = true;
    setPreparing(true);
    forgeAPI.prepareBid(tenderId)
      .then(() => refresh())
      .catch(() => {})
      .finally(() => setPreparing(false));
  }, [summary, tenderId, refresh]);

  return (
    <ForgeContext.Provider value={{ tenderId, summary, loading, error, preparing, refresh, setSummary }}>
      {children}
    </ForgeContext.Provider>
  );
};

export default ForgeContext;
